// Réglages du bureau (D-103) : ce que la page « Mentions légales et données » du club dit de lui — le
// responsable du traitement (nom officiel, siège), son représentant légal, l'e-mail où exercer ses droits.
// Vide : la page affiche « à compléter par le bureau ». Dans Club › « Page du club ».
import { clubLegal, legalSettings, saveLegalSettings, type LegalSettings } from '../lib/minors-legal';
import { legalMissing, REP_ROLES, type RepRole } from '../domain/legal';
import { $, html, mount, toast } from './dom';
import { hostClubSlug } from './legal-links';
import './legal.css';

const ROLE_LABEL: Record<RepRole, string> = {
  'président': 'Président', 'présidente': 'Présidente', 'représentant légal': 'Représentant légal', 'représentante légale': 'Représentante légale',
};

export async function renderLegalSettings(el: HTMLElement, clubId: string): Promise<void> {
  let s: LegalSettings;
  try { s = await legalSettings(clubId); } catch { mount(el, html``); return; }
  const info = await clubLegal({ club: clubId }).catch(() => null);
  const slug = info?.slug ?? null;
  // La page publique des données : le sous-domaine du club, sinon /c/<adresse>/donnees ; sans adresse, dans l'aide.
  const page = hostClubSlug() ? '/donnees' : slug ? `/c/${slug}/donnees` : '#/aide/donnees';
  const missing = legalMissing({ address: s.legal_address, rep: s.legal_rep, rep_role: s.legal_rep_role, data_email: s.data_email, contact_email: info?.contact_email ?? null });
  mount(el, html`
    <section class="block lg-admin" id="lg-admin">
      <div class="block-head"><h3>Mentions légales et données</h3>
        <span class="chip ${missing.length ? 'chip-warn' : 'chip-ok'}">${missing.length ? 'À compléter' : 'Complète'}</span></div>
      <p class="hint">La page que voient les familles depuis l’app, la page du club et chaque formulaire : le club y est responsable des données de ses membres, IDR l’héberge pour lui. Modèle à faire relire par un juriste.</p>
      ${missing.length ? html`<p class="small lg-missing">Il manque ${missing.join(', ')} : la page affiche « à compléter » à la place.</p>` : ''}
      <div class="pub-link"><a href="${page}" ${page.startsWith('#') ? '' : html`target="_blank" rel="noopener"`}>Voir la page</a></div>
      <form class="form" id="f-legal" novalidate>
        <label class="field"><span>Nom officiel du club (facultatif)</span><input name="legal_name" value="${s.legal_name ?? ''}" maxlength="160" placeholder="ex. Basket Club de Rivebelle (association)"></label>
        <label class="field"><span>Adresse du siège</span><input name="legal_address" value="${s.legal_address ?? ''}" maxlength="240" placeholder="ex. Mairie, 77999 Rivebelle"></label>
        <div class="row-2">
          <label class="field"><span>Représentant légal</span><input name="legal_rep" value="${s.legal_rep ?? ''}" maxlength="120" placeholder="Prénom Nom" autocomplete="off"></label>
          <label class="field"><span>Qualité</span><select name="legal_rep_role"><option value="">—</option>
            ${REP_ROLES.map(r => html`<option value="${r}" ${s.legal_rep_role === r ? 'selected' : ''}>${ROLE_LABEL[r]}</option>`)}</select></label>
        </div>
        <label class="field"><span>E-mail pour les données</span><input name="data_email" type="email" value="${s.data_email ?? ''}" maxlength="120" placeholder="ex. bureau@monclub.fr"></label>
        <p class="hint">Où chacun demande à voir, corriger ou effacer ses données. Vide : l’e-mail de contact public, s’il y en a un.</p>
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Enregistrer</button>
      </form>
    </section>`);

  const f = $<HTMLFormElement>(el, '#f-legal')!;
  f.addEventListener('submit', async (e) => {
    e.preventDefault();
    const d = new FormData(f);
    const v = (k: string) => String(d.get(k) ?? '').trim() || null;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    if (v('data_email') && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v('data_email')!)) { msg('E-mail pour les données : une adresse valide.'); return; }
    if (v('legal_rep') && !v('legal_rep_role')) { msg('Choisis la qualité du représentant légal (président, présidente…).'); return; }
    const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
    btn.disabled = true;
    try {
      await saveLegalSettings(clubId, { legal_name: v('legal_name'), legal_address: v('legal_address'), legal_rep: v('legal_rep'),
        legal_rep_role: v('legal_rep_role') as RepRole | null, data_email: v('data_email') });
      toast('Mentions légales enregistrées');
      await renderLegalSettings(el, clubId);
    } catch { btn.disabled = false; msg('Enregistrement impossible. Réessaie.'); }
  });
}
